/**
 * Canonical per-node-type field whitelist used by BOTH the diff engine and
 * the expanded config view. Keeps the two surfaces in lockstep so the review
 * never marks a node as changed unless a value in this whitelist actually
 * differs — the UI only renders these fields, and the diff only inspects
 * these fields.
 *
 * Mirrors the editable fields in each `<Type>NodeConfig.tsx` under
 * `apps/web/src/ui/components/node-config/`.
 */

export interface NodeFieldSpec {
  key: string;
  label: string;
  kind?: 'scalar' | 'kv' | 'inline_assertions' | 'json';
}

export const NODE_FIELDS: Record<string, NodeFieldSpec[]> = {
  login: [
    { key: 'credentialName', label: 'Credential' },
    { key: 'performLogin', label: 'Perform login' },
  ],
  navigate: [
    { key: 'pageUrl', label: 'Destination page' },
    { key: 'openInNewTab', label: 'Open in new tab' },
  ],
  action: [
    { key: 'customActionTitle', label: 'Title' },
    { key: 'actionDescription', label: 'Description' },
    { key: 'alwaysUseAgent', label: 'Always use agent' },
    { key: 'inputValues', label: 'Inputs', kind: 'kv' },
    { key: 'declaredOutputs', label: 'Outputs', kind: 'kv' },
    { key: 'inlineAssertions', label: 'Inline assertions', kind: 'inline_assertions' },
  ],
  assertion: [
    { key: 'condition', label: 'Condition' },
    { key: 'severity', label: 'Severity' },
    { key: 'alwaysUseAgent', label: 'Always use agent' },
  ],
  wait: [
    { key: 'delaySeconds', label: 'Delay' },
  ],
  condition: [
    { key: 'conditionDescription', label: 'Condition' },
    { key: 'evaluationHints', label: 'Hints', kind: 'json' },
  ],
  notification: [
    { key: 'channel', label: 'Channel' },
    { key: 'to', label: 'To' },
    { key: 'subject', label: 'Subject' },
    { key: 'body', label: 'Body' },
    { key: 'attachmentIds', label: 'Attachments', kind: 'json' },
    { key: 'attachmentFilenames', label: 'Attachment filenames', kind: 'json' },
  ],
  audio: [
    { key: 'label', label: 'Label' },
    { key: 'attachmentIds', label: 'Audio clip', kind: 'json' },
    { key: 'loop', label: 'Loop' },
    { key: 'waitForCompletion', label: 'Wait for completion' },
  ],
  api_sequence: [
    { key: 'description', label: 'Description' },
    { key: 'stopOnFailure', label: 'Stop on failure' },
    { key: 'steps', label: 'Steps', kind: 'json' },
    { key: 'exports', label: 'Exports', kind: 'json' },
  ],
  setup: [
    { key: 'setupFlowName', label: 'Setup flow' },
    { key: 'snapshotVersion', label: 'Version' },
    { key: 'executionMode', label: 'Execution mode' },
  ],
  seed: [
    { key: 'seedWorkflowName', label: 'Seed flow' },
    { key: 'snapshotVersion', label: 'Version' },
    { key: 'executionMode', label: 'Execution mode' },
    { key: 'onFailure', label: 'On failure' },
    { key: 'runTeardown', label: 'Run teardown' },
  ],
  mcp: [
    { key: 'propertyId', label: 'MCP application' },
    { key: 'instructions', label: 'Instructions' },
    { key: 'credentialTemplateId', label: 'Persona' },
    { key: 'credentialId', label: 'Credential' },
    { key: 'declaredOutputs', label: 'Outputs', kind: 'kv' },
    { key: 'toolAllowlist', label: 'Tool allowlist', kind: 'json' },
    { key: 'alwaysUseAgent', label: 'Always use agent' },
  ],
};

export function getNodeFields(nodeType: string): NodeFieldSpec[] {
  return NODE_FIELDS[nodeType] ?? [];
}

/**
 * Pick just the canonical fields from a configJson, dropping hydration-added
 * handlers, preview objects, and any other derived state. Returns a new
 * object so downstream diffing/rendering stays pure.
 */
export function pickCanonicalConfig(
  nodeType: string,
  config: Record<string, unknown> | null | undefined,
): Record<string, unknown> {
  if (!config) return {};
  const fields = getNodeFields(nodeType);
  if (fields.length === 0) return {};
  const out: Record<string, unknown> = {};
  for (const f of fields) {
    if (Object.prototype.hasOwnProperty.call(config, f.key)) {
      out[f.key] = config[f.key];
    }
  }
  return out;
}
